import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { Card } from '@/components/ui/card';
import { defaultDateRange } from '@/lib/date-range';
import { formatCompactCount, formatPercent } from '@/lib/format';
import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { QualityTrendChart } from '@/components/admin/ai-quality/quality-trend-chart';
import { LlmDateRangePicker } from '@/components/admin/llm/llm-date-range-picker';
import { WindowLabel } from '@/components/admin/llm/window-label';
import { StatTile } from '@/components/admin/shared/stat-tile';
import { useAdminAiQuality } from '@/hooks/data/admin/use-ai-quality';
import type { FeatureQualityRead } from '@/types/generated';
import { SECTION_TITLE, SUBHEADING } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

const searchSchema = z.object({
  start: z.string().date().optional(),
  end: z.string().date().optional(),
});

export const Route = createFileRoute('/_authenticated/admin/ai-quality/')({
  validateSearch: searchSchema,
  staticData: { title: 'AI quality' },
  component: AdminAiQualityPage,
});

const feedbackRatio = (helpful: number, total: number): string =>
  total === 0 ? '—' : formatPercent((helpful / total) * 100);

const scoreLabel = (feature: FeatureQualityRead): string =>
  feature.latest_overall === null ? '—' : feature.latest_overall.toFixed(2);

function AdminAiQualityPage(): React.ReactElement {
  const { start, end } = Route.useSearch();
  const navigate = Route.useNavigate();

  const defaultRange = useMemo(() => defaultDateRange(30), []);
  const rangeStart = start ?? defaultRange.start;
  const rangeEnd = end ?? defaultRange.end;

  const dashboardQuery = useAdminAiQuality({
    start: rangeStart,
    end: rangeEnd,
  });
  const dashboard = dashboardQuery.data;

  const handleApply = (range: { start?: string; end?: string }): void => {
    void navigate({
      search: (s) => ({ ...s, start: range.start, end: range.end }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="AI quality"
        description="Per-feature eval scores, error rate, and feedback over time"
      />

      <div className="flex flex-col gap-8 px-4 py-6 sm:px-8">
        {dashboard && (
          <WindowLabel
            start={dashboard.window_start}
            end={dashboard.window_end}
          />
        )}

        <LlmDateRangePicker
          key={`${rangeStart}-${rangeEnd}`}
          start={rangeStart}
          end={rangeEnd}
          onApply={handleApply}
        />

        {dashboardQuery.isLoading && (
          <p className="text-fg-3 text-fs-13">Loading…</p>
        )}

        {dashboardQuery.isError && (
          <p className="text-danger-700 text-fs-13">
            Failed to load AI quality data.
          </p>
        )}

        {dashboard && (
          <>
            <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-3">
              <StatTile
                label="Error rate"
                value={formatPercent(dashboard.error_rate.error_rate * 100)}
                hint="Share of LLM calls that failed over the window."
                sub={`${formatCompactCount(dashboard.error_rate.error_count)} of ${formatCompactCount(dashboard.error_rate.total_count)} calls`}
                tone={
                  dashboard.error_rate.error_rate > 0.05 ? 'danger' : 'default'
                }
              />
              <StatTile
                label="Helpful feedback"
                value={feedbackRatio(
                  dashboard.feedback.helpful,
                  dashboard.feedback.total,
                )}
                hint="Share of rated Copilot messages marked helpful."
                sub={`${formatCompactCount(dashboard.feedback.helpful)} of ${formatCompactCount(dashboard.feedback.total)} rated`}
              />
              <StatTile
                label="Features tracked"
                value={formatCompactCount(dashboard.features.length)}
                hint="Eval suites with at least one report in the window."
              />
            </div>

            <section className="flex flex-col gap-4">
              <h2 className={SECTION_TITLE}>Quality trend</h2>
              {dashboard.features.length === 0 && (
                <p className="text-fg-3 text-fs-13">
                  No eval reports in this window.
                </p>
              )}
              {dashboard.features.map((feature) => (
                <Card className="flex flex-col gap-3" key={feature.feature}>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className={cn(SUBHEADING, 'capitalize')}>
                      {feature.feature}
                    </h3>
                    <span
                      className={
                        feature.latest_passed === false
                          ? 'text-danger-700 text-fs-13 font-semibold tabular-nums'
                          : 'text-success-700 text-fs-13 font-semibold tabular-nums'
                      }
                    >
                      {scoreLabel(feature)}
                      {feature.latest_passed === false ? ' ⚠️' : ''}
                    </span>
                  </div>
                  <QualityTrendChart
                    points={feature.points}
                    threshold={feature.points.at(-1)?.threshold ?? 0}
                  />
                </Card>
              ))}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
